import React, { useContext, useState, useEffect, useRef } from "react";
import { useSpeakingDetector } from "../../hooks/useSpeakingDetector";
import { ThemeContext } from "../../contexts/ThemeContext";
import ParticipantAudio from "./ParticipantAudio";
import MuteComponent from "./MuteComponent";
import DegradedBadge from "./DegradedBadge";

export default function MeetingActiveSpeaker({ users, mediasTools, activeSpeakerId }) {
  const { localStream, degradedUserIds } = mediasTools;
  const { theme } = useContext(ThemeContext);
  const [color, setColor] = useState(theme === "dark" ? "#fff" : "#023377");
  const isSpeaking = useSpeakingDetector(localStream, false);

  const [pinnedUserId, setPinnedUserId] = useState(null);
  const [debouncedSpeakerId, setDebouncedSpeakerId] = useState(activeSpeakerId);
  const debounceRef = useRef(null);

  useEffect(() => {
    setColor(theme === "dark" ? "#fff" : "#023377");
  }, [theme]);

  // Debounce active speaker changes (1s)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSpeakerId(activeSpeakerId);
    }, 1000);
    return () => clearTimeout(debounceRef.current);
  }, [activeSpeakerId]);

  // Featured: pinned > debounced speaker > local user
  const pinned = pinnedUserId ? users.find((u) => u.userId === pinnedUserId) : null;
  const speaker = debouncedSpeakerId ? users.find((u) => u.userId === debouncedSpeakerId) : null;
  const localParticipant = users.find((u) => u.isLocal) || users[0];
  const featured = pinned || speaker || localParticipant;

  // n-1 : everyone except the featured user
  const thumbnails = users.filter((u) => u.userId !== featured?.userId);
  const shouldMarquee = thumbnails.length >= 3;
  // Duplicate items to fill enough width for smooth infinite scroll
  const repeatCount = thumbnails.length < 4 ? 4 : 2;
  const marqueeItems = shouldMarquee
    ? Array.from({ length: repeatCount }, () => thumbnails).flat()
    : thumbnails;
  const marqueeSpeed = marqueeItems.length * 2.5;

  const nameOverlayStyle = {
    position: "absolute",
    bottom: "8px",
    left: "8px",
    zIndex: 2,
    color: "#fff",
    fontWeight: 600,
    backgroundColor: "rgba(0,0,0,0.5)",
    padding: "2px 8px",
    borderRadius: "6px",
    pointerEvents: "none",
    maxWidth: "80%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const renderParticipant = (participant, isMain) => {
    const track = participant.stream?.getVideoTracks?.()[0];
    const speaking =
      (participant.isLocal && isSpeaking) ||
      (!participant.isLocal && activeSpeakerId === participant.userId);

    if (participant.videoOn && track) {
      return (
        <>
          <video
            ref={(ref) => {
              if (ref && ref.srcObject !== participant.stream) {
                ref.srcObject = participant.stream;
              }
            }}
            autoPlay
            muted={participant.isLocal}
            playsInline
            style={{
              transform: participant.isLocal ? "scaleX(-1)" : "none",
              objectFit: "cover",
              width: "100%",
              height: "100%",
              borderRadius: isMain ? "10px" : "8px",
            }}
          />
          <ParticipantAudio participant={participant} />
          <span style={{ ...nameOverlayStyle, fontSize: isMain ? "clamp(12px, 1.4vw, 15px)" : "10px" }}>
            {participant.displayName}
          </span>
        </>
      );
    }

    return (
      <div className="video-off-blur" style={{ height: "100%" }}>
        <ParticipantAudio participant={participant} />
        <img src={participant.userImage} alt="Background" className="blur-bg" />
        <div className={`avatar-wrapper ${speaking ? "speaking" : ""}`}>
          {participant.userImage ? (
            <img
              src={participant.userImage}
              alt="User"
              className={`user-avatar ${isMain ? "user-avatar-active-speaker" : ""}`}
              style={!isMain ? { width: "50px", height: "50px" } : undefined}
            />
          ) : (
            <div
              className={`user-avatar ${isMain ? "user-avatar-active-speaker" : ""}`}
              style={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
                fontSize: isMain ? "clamp(28px, 8vw, 50px)" : "16px",
                color,
                ...(isMain ? {} : { width: "50px", height: "50px" }),
              }}
            >
              {participant.displayName?.[0]}
              {participant.displayName?.split(" ")[1]?.charAt(0)}
            </div>
          )}
        </div>
        <span style={{ ...nameOverlayStyle, fontSize: isMain ? "clamp(12px, 1.4vw, 15px)" : "10px" }}>
          {participant.displayName}
        </span>
      </div>
    );
  };

  const renderThumb = (participant, keyPrefix = "", idx = 0) => {
    const speaking =
      (participant.isLocal && isSpeaking) ||
      (!participant.isLocal && activeSpeakerId === participant.userId);
    return (
      <div
        key={`${keyPrefix}thumb_${participant.userId}_${idx}`}
        className={`meeting-active-speaker-thumb ${
          pinnedUserId === participant.userId ? "thumb-pinned" : ""
        } ${speaking ? "thumb-speaking" : ""}`}
        onClick={() =>
          setPinnedUserId((prev) =>
            prev === participant.userId ? null : participant.userId
          )
        }
        style={{ position: "relative" }}
      >
        {!participant.audioOn && <MuteComponent />}
        {degradedUserIds?.includes(participant.userId) && (
          <DegradedBadge compact />
        )}
        {renderParticipant(participant, false)}
      </div>
    );
  };

  return (
    <div className="meeting-active-speaker-container">
      {/* Featured principal */}
      <div className="meeting-active-speaker-main" key={featured?.userId} style={{ position: "relative" }}>
        {featured && (
          <>
          {!featured.audioOn && <MuteComponent />}
          {degradedUserIds?.includes(featured?.userId) && <DegradedBadge />}
          {renderParticipant(featured, true)}
          </>
        )}
      </div>

      {/* Carousel marquee CSS */}
      <div
        className={`meeting-marquee-container ${
          !shouldMarquee ? "meeting-marquee-static" : ""
        }`}
      >
        <div
          className={`meeting-marquee-group ${
            shouldMarquee ? "meeting-marquee-animate" : ""
          }`}
          style={shouldMarquee ? { animationDuration: `${marqueeSpeed}s` } : {}}
        >
          {marqueeItems.map((p, i) => renderThumb(p, "a-", i))}
        </div>
        {shouldMarquee && (
          <div
            className="meeting-marquee-group meeting-marquee-animate"
            style={{ animationDuration: `${marqueeSpeed}s` }}
            aria-hidden="true"
          >
            {marqueeItems.map((p, i) => renderThumb(p, "b-", i))}
          </div>
        )}
      </div>
    </div>
  );
}
